import type { ClientOptions, QueryContext, QueryMiddleware } from './types';
import { base } from './base';
import { isResponseError } from './response-error';

interface ClientGlobalOptions {
  /**
   * Default headers applied to every request
   */
  headers: Record<string, string>;
  /**
   * Base URL to resolve relative URLs against
   *
   * Defaults to the current origin (window.location.origin)
   */
  baseURL?: string;
  /**
   * Error handler called when the client encounters an error
   * @param error - Error thrown by the client
   */
  onError: (error: unknown) => never;
  /**
   * Fetch implementation to use (defaults to the current global fetch)
   */
  fetch?: typeof fetch;
  middleware: QueryMiddleware[];
}

const globalOptions: ClientGlobalOptions = {
  headers: {},
  onError: (error: unknown) => {
    throw error;
  },
  middleware: [],
};

const PREFETCH_SKIPPED_STATUSES = new Set([503]);

export const client = async <TResponse = unknown>(
  input: string,
  options?: ClientOptions<TResponse>
): Promise<TResponse> => {
  const { isPrefetch, ...opts } = options ?? {};

  const headers = opts.headers instanceof Headers ? opts.headers : new Headers(opts.headers);

  Object.entries(globalOptions.headers).forEach(([key, value]) => {
    if (!headers.has(key)) {
      headers.set(key, value);
    }
  });

  const url = new URL(input, globalOptions.baseURL ?? window.location.origin);
  const fetch = globalOptions.fetch ?? globalThis.fetch;

  try {
    if (globalOptions.middleware.length === 0) {
      return await base<TResponse>(url, {
        ...opts,
        headers,
        fetch,
      });
    }

    const context: QueryContext<TResponse> = { opts, headers };

    const request = (): Promise<TResponse> =>
      base<TResponse>(url, {
        ...context.opts,
        headers: context.headers,
        fetch,
      });

    const runMiddleware = (index: number): Promise<TResponse> => {
      const middleware = globalOptions.middleware[index];

      if (!middleware) {
        return request();
      }

      return middleware(() => runMiddleware(index + 1), context);
    };

    return await runMiddleware(0);
  } catch (error) {
    if (isPrefetch && isResponseError(error) && PREFETCH_SKIPPED_STATUSES.has(error.status)) {
      throw error;
    }
    globalOptions.onError(error);
  }
};

client.init = (options: Partial<ClientGlobalOptions>): void => {
  Object.defineProperties(globalOptions, Object.getOwnPropertyDescriptors(options));
};

client.addMiddleware = (middleware: QueryMiddleware): void => {
  globalOptions.middleware.push(middleware);
};

client.getHeaders = (): Record<string, string> => {
  return { ...globalOptions.headers };
};
